'use client';

import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import Link from 'next/link';
import { LightningIcon } from 'components/icons/v2';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import { Button } from 'components/ui/button';
import { Avatar } from '../../../Avatar';
import { useAuth } from 'contexts/auth';
import { useToast } from 'hooks/useToast';
import { ReactedByNode, UserNode } from 'generated/types';
import { useSendHelpMessageResponseMutation } from 'generated/hooks';
import { useMemberTracking } from 'app/_tracking/events';
import { MEMBER_ROUTES } from 'utils/routes';
import LinkedinContext from '../../../../../_context/LinkedinContext';

interface INeedHelpButton {
  user: UserNode | null;
}

const SHOW_REACTED_AVATARS = 5;

export const NeedHelpBlock: FC<INeedHelpButton> = (props) => {
  const { user } = props;

  const [accordionValue, setAccordionValue] = useState<string>('');

  const [currentUserReacted, setCurrentUserReacted] = useState<boolean>(user?.needHelp?.currentUserReacted ?? false);
  const [reactedUsersLocal, setReactedUsersLocal] = useState<ReactedByNode[]>(props.user?.needHelp?.reactedByV2 ?? []);

  const { currentUser } = useAuth();
  const { setLinkedinModalState } = useContext(LinkedinContext);
  const { trackICanHelp } = useMemberTracking();

  const [helpReactState, sendCanHelpReaction] = useSendHelpMessageResponseMutation();

  const { handleToast } = useToast();

  const handleHelp = useCallback(async (user: UserNode | null) => {
    if (!currentUser?.profileStatus) {
      setLinkedinModalState(() => 'WELCOME');
      return;
    }

    try {
      const response = await sendCanHelpReaction({
        message: '',
        helpMessageId: user?.needHelp?.id ?? ''
      });

      if (response.error) {
        handleToast(response.error.message, 'error');
        console.error(response.error);
        return;
      }
      setCurrentUserReacted(prevState => {
        setReactedUsersLocal(prevStateUsers => !prevState ? [...prevStateUsers, {
          name: `${currentUser.firstName} ${currentUser.lastName}`,
          userId: currentUser.id,
          image: currentUser.image,
          __typename: 'ReactedByNode'
        }] : prevStateUsers.filter(user => user.userId !== currentUser.id));

        return !prevState;
      });

      trackICanHelp();

    } catch (error) {
      console.log(error);
    }
  }, [currentUser?.firstName, currentUser?.id, currentUser?.image, currentUser?.lastName, currentUser?.profileStatus, handleToast, sendCanHelpReaction, setLinkedinModalState, trackICanHelp]);

  const reactedUsersList = useMemo<[ReactedByNode | null, ReactedByNode[]]>(() => {
    const userReaction = reactedUsersLocal.find((user) => user.userId === currentUser?.id) || null;
    const otherReactions = reactedUsersLocal.filter((user) => user.userId !== currentUser?.id);

    return [userReaction, otherReactions];
  }, [currentUser?.id, reactedUsersLocal]);

  const reactedUserTitle: string = useMemo(() => {
    if (!currentUser) return '';
    return reactedUsersList[0] ? (reactedUsersList[1].length ? 'You and ' : 'You') : '';
  }, [currentUser, reactedUsersList]);

  return (
    <div>
      <div className={`rounded-t-radius-2xl gap-[10px] flex flex-col bg-lime-500 p-[18px]${reactedUsersLocal.length === 0 && user?.id === currentUser?.id ? ' rounded-b-radius-2xl' : ''}`}>
        <div className="flex justify-between items-center">
          <p className="text-x-large text-gray-950 font-bold">Shared</p>
        </div>
        {!!user?.needHelp?.message && (
          <p className="text-large text-gray-800 font-medium">
            {user.needHelp.message}
          </p>
        )}
      </div>
      <div className={`bg-white rounded-b-radius-2xl flex flex-col px-3 py-5 gap-3${reactedUsersLocal.length === 0 && user?.id === currentUser?.id ? ' hidden' : ''}`}>
        {!!reactedUsersLocal.length && (
          <Accordion
            type="single"
            collapsible
            disabled={reactedUsersList[1].length === 0}
            value={accordionValue}
            onValueChange={(value) => {
              setAccordionValue(() => value);
            }}
          >
            <AccordionItem value="users" className={currentUser && user?.id !== currentUser?.id &&'pb-3 !border-b-gray-100' || '!border-b-0'}>
              <AccordionTrigger className="!py-0" hideArrow={reactedUsersList[1].length === 0}>
                <div
                  className={`flex w-full items-center h-8 gap-[10px]`}>
                  <div
                    className="bg-black rounded-full text-white h-[22px] w-[22px] flex items-center justify-center px-1 py-[6px]">
                    <LightningIcon />
                  </div>
                  <p className="text-small text-gray-800">{reactedUserTitle}</p>
                  {!accordionValue && (
                    <>
                      <div className="flex">
                        {[...reactedUsersList[1]].splice(0, SHOW_REACTED_AVATARS).map((user, index) => (
                          <div style={{ transform: `translateX(-${index * 12}px)` }} key={index}>
                            <Avatar
                              height={32}
                              width={32}
                              user={{
                                firstName: user.name.split(' ')[0],
                                lastName: user.name.split(' ')[1],
                                id: user.userId,
                                image: user.image
                              } as UserNode}
                              avatar={user.image}
                              alt={user.name}
                              containerClassname="border-[#DADADA] border"
                            />
                          </div>
                        ))}
                      </div>
                      {reactedUsersList[1].length - SHOW_REACTED_AVATARS > 0 && (
                        <div
                          className="w-8 h-8 rounded-full bg-brand-300 flex items-center justify-center text-small text-brand-700 font-medium"
                          style={{ transform: `translateX(-${([...reactedUsersList[1]].splice(0, SHOW_REACTED_AVATARS).length - 1) * 12}px)` }}
                        >
                          +{reactedUsersList[1].length - SHOW_REACTED_AVATARS}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="!py-2">
                <div className="flex gap-2 flex-col w-full max-h-[152px] overflow-auto">
                  {reactedUsersList[1].map((user, index) => (
                    <Link href={MEMBER_ROUTES.MEMBER_PROFILE.getPath(user.userId)} className="flex gap-3 items-center" key={index}>
                      <Avatar
                        height={32}
                        width={32}
                        user={{
                          firstName: user.name.split(' ')[0],
                          lastName: user.name.split(' ')[1],
                          id: user.userId,
                          image: user.image
                        } as UserNode}
                        avatar={user.image}
                        alt={user.name}
                        containerClassname="border-[#DADADA] border"
                      />
                      <p className="text-small text-gray-700 font-medium">{user.name}</p>
                    </Link>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
        {currentUser && user?.id !== currentUser?.id && (
          <Button
            variant="primaryBrand"
            size="medium"
            className={`cursor-pointer self-start flex gap-[10px] ${currentUserReacted ?
              'bg-black border-black text-white hover:bg-black/80 hover:border-black/80' :
              'bg-[#D9D9D9] border-[#D9D9D9] !text-black hover:bg-[#D9D9D9]/80 hover:border-[#D9D9D9]/80'}`
            }
            onClick={() => handleHelp(user)}
          >
            <div className="h-6 w-6 px-1 py-[2px] flex items-center justify-center">
              <LightningIcon className="fill-brand-500" />
            </div>
            Interested
          </Button>
        )}
      </div>
    </div>
  );
};
